import { StaticImage } from 'gatsby-plugin-image';
import moment from 'moment';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { useSignals } from '@preact/signals-react/runtime';
import { AnimatePresence, motion } from 'motion/react';
import { Chip } from '@/features/details/shared';
import { HSpace } from '@/components/spacing';
import { Opt } from '@/features/details/date-pick/quick-range-select';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { isTimezoneBehindUTC } from '@/utils/time-utils';

type Props = {
  onClick: () => void;
  whiteBg?: boolean;
};
const ChipWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 322px;
`;

export const DatePickerChip = React.forwardRef(
  ({ onClick, whiteBg }: Props, ref) => {
    useSignals();
    const { startDate, endDate, step, version } = useContext(DatePickerContext);
    let message = ``;
    switch (step?.value) {
      case Opt.Current:
        message = `Latest Version:`;
        break;
      case Opt.CustomRange:
        message = `Custom:`;
        break;
      case Opt.LastWeek:
        message = `Last Week:`;
        break;
      case Opt.LastMonth:
        message = `Last Month:`;
        break;
      case Opt.Today:
        message = `Today:`;
        break;
      case Opt.Yesterday:
        message = `Yesterday:`;
        break;
      case Opt.OverAll:
        message = `Overall:`;
        break;
      case Opt.PreviousVersion:
        if (version?.value?.isCurrent) {
          message = `Latest Version:`;
        } else {
          message = `Version ${
            version?.value?.version ? `(${version?.value?.version})` : ``
          }:`;
        }
        break;
      default:
    }
    const marginRight = `1rem`;
    return (
      <ChipWrapper ref={ref as any}>
        <Chip whiteBg={whiteBg} onClick={onClick} layout>
          <motion.div layout style={{ display: `flex`, alignItems: `center` }}>
            <StaticImage
              style={{ marginRight }}
              src="calendar.svg"
              alt="calendar"
              placeholder="none"
              loading="eager"
              formats={[`png`, `webp`]}
              height={10}
            />
          </motion.div>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              layout
              key={`${message}-${startDate?.value}-${endDate?.value}`}
              initial={{ opacity: 0, filter: `blur(4px)` }}
              animate={{ opacity: 1, filter: `blur(0px)` }}
              exit={{ opacity: 0, filter: `blur(4px)` }}
              transition={{ duration: 0.2 }}
              style={{
                display: `inline-flex`,
                alignItems: `center`,
                whiteSpace: `nowrap`,
              }}
            >
              {message} {formatDuration(startDate?.value, endDate?.value)}
            </motion.span>
          </AnimatePresence>
          <DownIcon />
        </Chip>
      </ChipWrapper>
    );
  },
);

export function DownIcon() {
  return (
    <motion.span
      layout
      style={{ display: `inline-flex`, alignItems: `center` }}
    >
      <HSpace value={1.5} />
      <StaticImage
        src="../../../assets/down_arrow.svg"
        alt="end"
        placeholder="none"
        loading="eager"
        height={4}
      />
    </motion.span>
  );
}

export function formatDuration(
  startDater: Date | string,
  endDate: Date | string,
) {
  return (
    <>
      {isTimezoneBehindUTC()
        ? moment.utc(startDater)?.format(`  MMM D, YYYY`)
        : moment(startDater)?.format(`  MMM D, YYYY`)}
      {` `}
      {` - `}
      {isTimezoneBehindUTC()
        ? moment.utc(endDate)?.format(`  MMM D, YYYY`)
        : moment(endDate)?.format(`  MMM D, YYYY`)}
    </>
  );
}
